import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import PageBanner from '@/components/site/PageBanner';
import SEO from '@/components/site/SEO';

const API = process.env.REACT_APP_API_URL || '';

const DEPARTMENTS = [
  { value: 'sales',      label: 'Sales' },
  { value: 'telesales',  label: 'Tele-Sales' },
  { value: 'hr',         label: 'HR & Recruitment' },
  { value: 'operations', label: 'Operations' },
  { value: 'other',      label: 'Other' },
];

const QUALIFICATIONS = [
  'SSC (10th)',
  'HSC (12th)',
  'Diploma',
  "Bachelor's Degree (B.A / B.Com / B.Sc / BBA / BCA / B.Tech)",
  "Master's Degree (M.A / M.Com / MBA / M.Tech)",
  'CA / CS / CFA',
  'Other',
];

const TEST_CENTERS = ['Kolshet', 'Naupada', 'Work From Home'];

const EMPTY = {
  full_name: '', phone: '', email: '', marital_status: '',
  native_place: '', address: '',
  department: '', designation_applied: '', job_role: '',
  current_salary: '', expected_salary: '',
  highest_qualification: '', test_center: '',
  last_company: '', total_experience: '',
  currently_employed: '', employment_gap: '',
  immediate_joiner: '', notice_period: '',
  reason_for_leaving: '',
};

export default function AssessmentRegister() {
  const navigate = useNavigate();
  const [step,    setStep]    = useState(1);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState('');
  const [form,    setForm]    = useState(EMPTY);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const goStep2 = e => {
    e.preventDefault(); setError('');
    if (!form.full_name.trim()) { setError('Full name is required.'); return; }
    if (!/^[0-9]{10}$/.test(form.phone.replace(/\s/g, ''))) {
      setError('Please enter a valid 10-digit mobile number.'); return;
    }
    setStep(2);
  };

  const goStep3 = e => { e.preventDefault(); setError(''); setStep(3); };

  const submit = async e => {
    e.preventDefault();
    setLoading(true); setError('');
    try {
      const res = await axios.post(`${API}/api/assessment/register`, form);
      localStorage.setItem('finirio_assess_id',   String(res.data.attempt_id));
      localStorage.setItem('finirio_assess_tok',  res.data.session_token);
      localStorage.setItem('finirio_assess_name', res.data.candidate_name);
      navigate('/assessment/rules');
    } catch (err) {
      setError(err.response?.data?.detail || 'Registration failed. Please try again.');
    } finally { setLoading(false); }
  };

  const STEPS = ['Personal Details', 'Professional Details', 'Experience'];

  return (
    <main>
      <SEO title="Candidate Assessment — Finirio" path="/assessment"
        description="Register to take the Finirio aptitude assessment for career opportunities." />
      <PageBanner title="Candidate Assessment" crumbs={['Careers', 'Assessment']} />

      <section className="py-12 bg-white">
        <div className="max-w-[640px] mx-auto px-4">

          {/* Stepper */}
          <div className="flex items-center gap-2 mb-8">
            {STEPS.map((label, i) => {
              const n = i + 1; const done = step > n; const active = step === n;
              return (
                <React.Fragment key={n}>
                  <div className="flex items-center gap-2 shrink-0">
                    <div className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-bold transition-colors ${done || active ? 'text-white' : 'bg-slate-100 text-slate-400'}`}
                      style={done || active ? { backgroundColor: '#1e73be' } : {}}>
                      {done ? '✓' : n}
                    </div>
                    <span className={`text-sm font-medium hidden sm:inline ${active || done ? 'text-slate-800' : 'text-slate-400'}`}>{label}</span>
                  </div>
                  {i < STEPS.length - 1 && (
                    <div className="flex-1 h-0.5 bg-slate-200 relative">
                      <div className="absolute inset-y-0 left-0 transition-all"
                        style={{ width: step > n ? '100%' : '0%', backgroundColor: '#1e73be' }} />
                    </div>
                  )}
                </React.Fragment>
              );
            })}
          </div>

          {error && (
            <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">{error}</div>
          )}

          {/* ── Step 1: Personal Details ── */}
          {step === 1 && (
            <form onSubmit={goStep2} className="space-y-4">
              <h2 className="text-xl font-semibold text-slate-800 mb-2">Personal Details</h2>

              <Field label="Full Name" required>
                <input required value={form.full_name} onChange={e => set('full_name', e.target.value)}
                  placeholder="Enter your full name" className={inp} />
              </Field>

              <Field label="Mobile Number" required>
                <input required value={form.phone} onChange={e => set('phone', e.target.value)}
                  placeholder="10-digit mobile number" maxLength={10} className={inp} />
              </Field>

              <Field label="Email Address">
                <input type="email" value={form.email} onChange={e => set('email', e.target.value)}
                  placeholder="Enter your email address (optional)" className={inp} />
              </Field>

              <Field label="Marital Status">
                <select value={form.marital_status} onChange={e => set('marital_status', e.target.value)} className={sel}>
                  <option value="">-- Select --</option>
                  {['Single','Married','Divorced','Widowed'].map(m => <option key={m} value={m}>{m}</option>)}
                </select>
              </Field>

              <Field label="Highest Qualification">
                <select value={form.highest_qualification} onChange={e => set('highest_qualification', e.target.value)} className={sel}>
                  <option value="">-- Select Qualification --</option>
                  {QUALIFICATIONS.map(q => <option key={q} value={q}>{q}</option>)}
                </select>
              </Field>

              <Field label="Native Place">
                <input value={form.native_place} onChange={e => set('native_place', e.target.value)}
                  placeholder="e.g. Nashik, Kolhapur" className={inp} />
              </Field>

              <Field label="Current Residential Address">
                <textarea value={form.address} onChange={e => set('address', e.target.value)}
                  rows={2} placeholder="Enter your current residential address" className={inp + ' resize-none py-2'} />
              </Field>

              <Field label="Preferred Test Center" required>
                <div className="flex gap-3">
                  {TEST_CENTERS.map(c => (
                    <label key={c} className={`flex-1 h-12 rounded-lg border-2 flex items-center justify-center cursor-pointer text-sm font-medium transition
                      ${form.test_center === c ? 'border-[#1e73be] bg-[#eaf3fb] text-[#1e73be]' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                      <input type="radio" className="sr-only" name="test_center" value={c}
                        checked={form.test_center === c} onChange={() => set('test_center', c)} />
                      📍 {c}
                    </label>
                  ))}
                </div>
              </Field>

              <NavBtns onBack={null} nextLabel="Next →" />
            </form>
          )}

          {/* ── Step 2: Professional Details ── */}
          {step === 2 && (
            <form onSubmit={goStep3} className="space-y-4">
              <h2 className="text-xl font-semibold text-slate-800 mb-2">Professional Details</h2>

              <Field label="Department Applying For">
                <select value={form.department} onChange={e => set('department', e.target.value)} className={sel}>
                  <option value="">-- Select Department --</option>
                  {DEPARTMENTS.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </Field>

              <Field label="Position Applied For">
                <input value={form.designation_applied} onChange={e => set('designation_applied', e.target.value)}
                  placeholder="e.g. Relationship Manager, Sales Executive" className={inp} />
              </Field>

              <Field label="Current / Last Job Title">
                <input value={form.job_role} onChange={e => set('job_role', e.target.value)}
                  placeholder="Your current or most recent job title" className={inp} />
              </Field>

              <div className="grid grid-cols-2 gap-3">
                <Field label="Current Salary (₹ / month)">
                  <input value={form.current_salary} onChange={e => set('current_salary', e.target.value)}
                    placeholder="e.g. 25000" className={inp} />
                </Field>
                <Field label="Expected Take-Home (₹ / month)">
                  <input value={form.expected_salary} onChange={e => set('expected_salary', e.target.value)}
                    placeholder="e.g. 35000" className={inp} />
                </Field>
              </div>

              <NavBtns onBack={() => setStep(1)} nextLabel="Next →" />
            </form>
          )}

          {/* ── Step 3: Experience ── */}
          {step === 3 && (
            <form onSubmit={submit} className="space-y-4">
              <h2 className="text-xl font-semibold text-slate-800 mb-2">Work Experience</h2>

              <Field label="Last / Current Company Name">
                <input value={form.last_company} onChange={e => set('last_company', e.target.value)}
                  placeholder="e.g. HDFC Bank, Bajaj Finance, Freshers may leave blank" className={inp} />
              </Field>

              <Field label="Total Work Experience">
                <input value={form.total_experience} onChange={e => set('total_experience', e.target.value)}
                  placeholder="e.g. 2 years 6 months  /  Fresher" className={inp} />
              </Field>

              <Field label="Are you currently employed?">
                <div className="flex gap-3">
                  {[['yes','Yes, currently working'],['no','No, not working']].map(([v, label]) => (
                    <label key={v} className={`flex-1 h-12 rounded-lg border-2 flex items-center justify-center cursor-pointer text-sm font-medium transition
                      ${form.currently_employed === v ? 'border-[#1e73be] bg-[#eaf3fb] text-[#1e73be]' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                      <input type="radio" className="sr-only" name="currently_employed" value={v}
                        checked={form.currently_employed === v} onChange={() => set('currently_employed', v)} />
                      {label}
                    </label>
                  ))}
                </div>
              </Field>

              {form.currently_employed === 'yes' && (
                <>
                  <Field label="Notice Period">
                    <select value={form.notice_period} onChange={e => set('notice_period', e.target.value)} className={sel}>
                      <option value="">-- Select Notice Period --</option>
                      {['Immediate','15 Days','1 Month','2 Months','3 Months','More than 3 Months'].map(p =>
                        <option key={p} value={p}>{p}</option>)}
                    </select>
                  </Field>
                  <Field label="Reason for Leaving Current Job">
                    <textarea value={form.reason_for_leaving} onChange={e => set('reason_for_leaving', e.target.value)}
                      rows={2} placeholder="Please describe why you are looking for a change" className={inp + ' resize-none py-2'} />
                  </Field>
                </>
              )}

              {form.currently_employed === 'no' && (
                <>
                  <Field label="Reason for Leaving Last Job">
                    <textarea value={form.reason_for_leaving} onChange={e => set('reason_for_leaving', e.target.value)}
                      rows={2} placeholder="Please describe why you left your last company" className={inp + ' resize-none py-2'} />
                  </Field>
                  <Field label="Any gap in employment? Please explain">
                    <textarea value={form.employment_gap} onChange={e => set('employment_gap', e.target.value)}
                      rows={2} placeholder="e.g. Family responsibilities, higher studies, health reasons, etc." className={inp + ' resize-none py-2'} />
                  </Field>
                </>
              )}

              <Field label="Are you an Immediate Joiner?">
                <div className="flex gap-3">
                  {[['yes','Yes, I can join immediately'],['no','No, I require notice period']].map(([v, label]) => (
                    <label key={v} className={`flex-1 h-12 rounded-lg border-2 flex items-center justify-center cursor-pointer text-sm font-medium transition
                      ${form.immediate_joiner === v ? 'border-[#2ea690] bg-[#eafaf6] text-[#2ea690]' : 'border-slate-200 text-slate-600 hover:bg-slate-50'}`}>
                      <input type="radio" className="sr-only" name="immediate_joiner" value={v}
                        checked={form.immediate_joiner === v} onChange={() => set('immediate_joiner', v)} />
                      {label}
                    </label>
                  ))}
                </div>
              </Field>

              <NavBtns onBack={() => setStep(2)} nextLabel={loading ? 'Registering…' : 'Register & Proceed →'} disabled={loading} isSubmit />
            </form>
          )}

          <p className="mt-6 text-xs text-center text-slate-400">
            Each candidate may attempt the test only once. Duplicate attempts are blocked by mobile number.
          </p>
        </div>
      </section>
    </main>
  );
}

const inp = 'w-full h-11 px-3 border border-slate-300 rounded-lg outline-none focus:ring-2 focus:ring-[#1e73be]/30 focus:border-[#1e73be] text-slate-800 text-sm';
const sel = inp + ' bg-white';

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-medium text-slate-700 mb-1">
      {label}{required && <span className="text-red-500 ml-1">*</span>}
    </label>
    {children}
  </div>
);

const NavBtns = ({ onBack, nextLabel, disabled, isSubmit }) => (
  <div className="flex gap-3 pt-2">
    {onBack && (
      <button type="button" onClick={onBack}
        className="flex-1 h-12 rounded-lg border border-slate-300 text-slate-700 font-medium hover:bg-slate-50 transition">
        ← Back
      </button>
    )}
    <button type="submit" disabled={disabled}
      className="flex-1 h-12 rounded-lg text-white font-semibold hover:brightness-110 transition disabled:opacity-60"
      style={{ backgroundColor: '#1e73be' }}>
      {nextLabel}
    </button>
  </div>
);
